/**
 * ParametresV2 — Cockpit de configuration transverse (admin uniquement).
 *
 * Réservée aux admins (côté UI ; les écritures passent par les hooks useV1Write).
 * Ne duplique PAS les modules dédiés : la gestion des membres/rôles vit dans
 * « Équipe », la charte dans « Brand Center ». Paramètres regroupe la
 * configuration qui n'a pas de page à elle.
 *
 * Lot 1 : onglet « Clubs & périmètre » (CRUD ep:clubs + rattachement des IDs
 * de reporting GA4 / Metricool par club). Les autres onglets sont des
 * emplacements réservés, branchés dans des lots ultérieurs.
 */
import React, { useMemo, useState } from "react";
import { useClubs } from "../hooks/useV1Data";
import { createClub, updateClub, setClubArchived } from "../hooks/useV1Write";
import { isAdmin as isAdminUser } from "../lib/roles";
import {
  IconSettings, IconUsers, IconMetricool, IconBarChart, IconBell,
  IconPlus, IconX, IconExternalLink,
} from "../icons";

const ACCENT = "#0071CD";

const TABS = [
  { id: "clubs",         label: "Clubs & périmètre", Icon: IconUsers },
  { id: "integrations",  label: "Intégrations",      Icon: IconMetricool },
  { id: "demandes",      label: "Demandes",          Icon: IconBarChart },
  { id: "notifications", label: "Notifications",     Icon: IconBell },
];

/* ─── styles partagés ─── */
const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #E2E8F0",
  fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: "#F8FAFC", outline: "none",
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: "var(--text, #1a1a1c)", margin: "0 0 8px" };
const hintStyle = { fontWeight: 400, color: "#94A3B8", fontSize: 12 };

/* ─────────────────────────── Modal création / édition club ─────────────────── */
function ClubModal({ club, onClose, onSaved }) {
  const editing = !!club;
  const [name, setName]       = useState(club?.name || club?.nom || "");
  const [ville, setVille]     = useState(club?.ville || "");
  const [ga4, setGa4]         = useState(club?.ga4PropertyId || "");
  const [metricool, setMetricool] = useState(club?.metricoolBlogId || "");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  async function handleSubmit() {
    if (!name.trim()) { setError("Le nom du club est obligatoire."); return; }
    setSaving(true);
    setError(null);
    const payload = {
      name: name.trim(),
      ville: ville.trim(),
      ga4PropertyId: ga4.trim(),
      metricoolBlogId: metricool.trim(),
    };
    try {
      if (editing) await updateClub(club.id, payload);
      else await createClub(payload);
      onSaved();
    } catch (e) {
      const msg = String((e && e.message) || "");
      setError(msg.includes("403")
        ? "Écriture refusée : action réservée aux administrateurs."
        : "L'enregistrement a échoué. Vérifie ta connexion et réessaie.");
      setSaving(false);
    }
  }

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(26,26,28,.5)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000,
  };
  const card = {
    width: "min(520px,100%)", maxHeight: "90vh", overflowY: "auto",
    background: "#fff", borderRadius: 14, padding: "20px 22px",
    boxShadow: "0 16px 48px rgba(0,0,0,.24)", fontFamily: "inherit",
  };
  const field = { marginBottom: 16 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "var(--text, #1a1a1c)" }}>
            {editing ? "Modifier le club" : "Ajouter un club"}
          </h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", lineHeight: 1, padding: 2 }}>
            <IconX size={20} />
          </button>
        </div>

        {/* Nom + Ville */}
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...field, flex: 2 }}>
            <p style={labelStyle}>Nom du club</p>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputBase} placeholder="Esprit Padel Lyon" />
          </div>
          <div style={{ ...field, flex: 1 }}>
            <p style={labelStyle}>Ville</p>
            <input value={ville} onChange={(e) => setVille(e.target.value)} style={inputBase} placeholder="Lyon" />
          </div>
        </div>

        {/* Section reporting */}
        <div style={{ borderTop: "1px solid #EEF2F7", margin: "4px 0 16px", paddingTop: 16 }}>
          <p style={{ fontSize: 12.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em", margin: "0 0 12px" }}>
            Reporting
          </p>

          <div style={field}>
            <p style={labelStyle}>ID propriété GA4 <span style={hintStyle}>· branche le « Web » au reporting</span></p>
            <input value={ga4} onChange={(e) => setGa4(e.target.value)} style={inputBase} placeholder="ex. 312345678" inputMode="numeric" />
          </div>

          <div style={field}>
            <p style={labelStyle}>ID blog Metricool <span style={hintStyle}>· réseaux IG / FB / GMB</span></p>
            <input value={metricool} onChange={(e) => setMetricool(e.target.value)} style={inputBase} placeholder="ex. 4483840" inputMode="numeric" />
          </div>
        </div>

        {error && <p style={{ fontSize: 13, color: "#B23B3B", margin: "4px 0 0" }}>{error}</p>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #CBD5E1", background: "transparent", fontSize: 14, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: saving ? "#94A3B8" : ACCENT, color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Ligne club ─────────────────────────────────────── */
function ClubRow({ club, onEdit, onToggleArchived }) {
  const archived = club.archived === true;
  const name = club.name || club.nom || "Sans nom";
  const chips = [];
  if (club.ga4PropertyId) chips.push({ label: "GA4", color: "#0E9F6E" });
  if (club.metricoolBlogId) chips.push({ label: "Metricool", color: "#D97706" });

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
      borderBottom: "1px solid #EEF2F7", opacity: archived ? 0.55 : 1,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text, #1a1a1c)" }}>
          {name}
          {archived && <span style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 500 }}> · archivé</span>}
        </div>
        <div style={{ fontSize: 12.5, color: "#64748B" }}>{club.ville || "—"}</div>
      </div>

      {/* Chips reporting */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end", maxWidth: 220 }}>
        {chips.length ? chips.map((c, i) => (
          <span key={i} style={{ fontSize: 11, fontWeight: 700, color: c.color, background: `${c.color}14`, borderRadius: 6, padding: "2px 8px" }}>{c.label}</span>
        )) : (
          <span style={{ fontSize: 11.5, color: "#CBD5E1" }}>Reporting non configuré</span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button onClick={() => onEdit(club)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}>
          Modifier
        </button>
        <button onClick={() => onToggleArchived(club)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", color: archived ? ACCENT : "#B23B3B" }}>
          {archived ? "Restaurer" : "Archiver"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Onglet Clubs ───────────────────────────────────── */
function ClubsTab() {
  const { clubs } = useClubs();
  const [modalClub, setModalClub] = useState(null); // objet | {} (nouveau) | null (fermé)
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const list = useMemo(() => {
    const arr = (clubs || []).filter(Boolean);
    const visible = showArchived ? arr : arr.filter((c) => c.archived !== true);
    return visible.slice().sort((a, b) =>
      String(a.name || a.nom || "").localeCompare(String(b.name || b.nom || ""))
    );
  }, [clubs, showArchived]);

  const archivedCount = (clubs || []).filter((c) => c && c.archived === true).length;

  async function handleToggleArchived(c) {
    setBusyId(c.id);
    try { await setClubArchived(c.id, c.archived !== true); } catch { /* onSnapshot gère le rafraîchissement */ }
    setBusyId(null);
  }

  return (
    <div>
      <p style={{ fontSize: 13.5, color: "#64748B", margin: "0 0 16px", lineHeight: 1.55 }}>
        Les clubs structurent tout le périmètre (demandes, tâches, reporting). Associez à chaque
        club son <strong>ID GA4</strong> (pour le Web) et son <strong>ID Metricool</strong> (réseaux)
        afin que KPI&nbsp;&amp;&nbsp;Reporting agrège les bonnes données. L'entité « national »
        se gère côté serveur.
      </p>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
        <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>
          {list.length} club{list.length > 1 ? "s" : ""}{showArchived ? "" : " actif" + (list.length > 1 ? "s" : "")}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {archivedCount > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#64748B", cursor: "pointer" }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Afficher les archivés ({archivedCount})
            </label>
          )}
          <button onClick={() => setModalClub({})}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, border: "none", background: ACCENT, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <IconPlus size={15} /> Ajouter un club
          </button>
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #EEF2F7", borderRadius: 12, overflow: "hidden" }}>
        {list.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", fontSize: 13.5, color: "#94A3B8" }}>
            Aucun club à afficher.
          </div>
        ) : (
          list.map((c) => (
            <div key={String(c.id)} style={{ pointerEvents: busyId === c.id ? "none" : "auto" }}>
              <ClubRow club={c} onEdit={(x) => setModalClub(x)} onToggleArchived={handleToggleArchived} />
            </div>
          ))
        )}
      </div>

      {modalClub !== null && (
        <ClubModal
          club={modalClub && modalClub.id != null ? modalClub : null}
          onClose={() => setModalClub(null)}
          onSaved={() => setModalClub(null)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── Onglets à venir ────────────────────────────────── */
function ComingSoon({ title, children }) {
  return (
    <div style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 12, padding: "32px 28px", textAlign: "center" }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text, #1a1a1c)", marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.55, maxWidth: 460, margin: "0 auto" }}>{children}</div>
      <div style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: ACCENT, background: "#EFF6FF", borderRadius: 999, padding: "4px 12px" }}>
        Bientôt
      </div>
    </div>
  );
}

function IntegrationsTab() {
  return (
    <ComingSoon title="État des intégrations">
      État de connexion de <strong>Metricool</strong> (mapping club ↔ blog, dernière synchro),
      de <strong>GA4</strong> (compte de service, propriétés) et de l'email
      <strong> demande-comm@</strong> (SMTP / IMAP). Lecture seule&nbsp;: les secrets restent
      côté serveur (<code>.env</code>). Vous pouvez déjà en attendant ouvrir Metricool depuis la barre du haut.
      <div style={{ marginTop: 14 }}>
        <a href="https://app.metricool.com" target="_blank" rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: ACCENT, textDecoration: "none", fontWeight: 600 }}>
          Ouvrir Metricool <IconExternalLink size={13} />
        </a>
      </div>
    </ComingSoon>
  );
}

function DemandesTab() {
  return (
    <ComingSoon title="Demandes & formulaire public">
      Catégories de demandes, attributaire par défaut, délai cible (SLA), modèle de l'accusé
      de réception envoyé via <strong>demande-comm@</strong> et champs visibles du formulaire public.
    </ComingSoon>
  );
}

function NotificationsTab() {
  return (
    <ComingSoon title="Notifications & digest">
      Destinataires du digest quotidien des demandes, fréquence et canal. La relève email
      tourne déjà côté serveur&nbsp;; cet onglet exposera qui reçoit quoi.
    </ComingSoon>
  );
}

/* ─────────────────────────── Page ──────────────────────────────────────────── */
export default function ParametresV2({ currentUser }) {
  const [tab, setTab] = useState("clubs");
  const isAdmin = isAdminUser(currentUser);

  if (!isAdmin) {
    return (
      <div style={{ padding: "48px 28px", display: "flex", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <div style={{ color: "#CBD5E1", marginBottom: 12, display: "flex", justifyContent: "center" }}>
            <IconSettings size={40} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text, #1a1a1c)", marginBottom: 6 }}>Accès réservé</div>
          <div style={{ fontSize: 13.5, color: "#64748B", lineHeight: 1.5 }}>
            Les paramètres de l'application sont réservés aux administrateurs. Contacte le
            responsable communication pour toute modification.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>
      {/* Barre d'onglets */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #EEF2F7", marginBottom: 22, flexWrap: "wrap" }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "9px 14px",
                border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
                fontSize: 13.5, fontWeight: active ? 700 : 500,
                color: active ? ACCENT : "#64748B",
                borderBottom: active ? `2px solid ${ACCENT}` : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              <t.Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "clubs" && <ClubsTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "demandes" && <DemandesTab />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}
